import { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';

import { AnalyticsEmpty, AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import AnalyticsTabs from '../../components/AnalyticsTabs';
import { SectionSubtitle, SectionTitle } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';

import { OPERATIONS_DOCUMENTS_UPLOADED } from '../../constants/metricDescriptions';
import useRoleTypeTabs, { ROLE_TYPE_LABELS } from '../../hooks/useRoleTypeTabs';
import { getApplicantDocumentUploads, getPractitionerDocumentUploads } from 'api/rest/analytics';
import { AnalyticsRoleType } from 'hooks/analytics';

const CLIENT_TAB = 'client' as const;
type DocumentsUploadedTab = AnalyticsRoleType | typeof CLIENT_TAB;

const TAB_LABELS: Record<DocumentsUploadedTab, string> = {
    ...ROLE_TYPE_LABELS,
    [CLIENT_TAB]: 'Clients',
};

type DocumentsUploadedRow = {
    id: number;
    name: string;
    documentsUploaded: number;
};

type SortDirection = 'asc' | 'desc';
const DEFAULT_DIRECTION: SortDirection = 'desc';

interface DocumentsUploadedProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const DocumentsUploaded = ({ from, to, serviceProviderId }: DocumentsUploadedProps) => {
    const { allowedTabs: allowedRoleTabs, defaultTab: defaultRoleTab } = useRoleTypeTabs();

    const documentsUploadedTabs = useMemo<DocumentsUploadedTab[]>(
        () => (allowedRoleTabs.length > 0 ? [...allowedRoleTabs, CLIENT_TAB] : []),
        [allowedRoleTabs]
    );

    const [selectedTab, setSelectedTab] = useState<DocumentsUploadedTab | null>(defaultRoleTab);
    const [uploads, setUploads] = useState<DocumentsUploadedRow[]>([]);
    const [loading, setLoading] = useState<boolean>(documentsUploadedTabs.length > 0);
    const [error, setError] = useState<string | null>(null);
    const [direction, setDirection] = useState<SortDirection>(DEFAULT_DIRECTION);

    useEffect(() => {
        if (selectedTab && !documentsUploadedTabs.includes(selectedTab)) {
            setSelectedTab(defaultRoleTab);
        }
    }, [documentsUploadedTabs, selectedTab, defaultRoleTab]);

    useEffect(() => {
        if (!selectedTab || !documentsUploadedTabs.includes(selectedTab)) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);

        const request =
            selectedTab === CLIENT_TAB
                ? getApplicantDocumentUploads(from, to, serviceProviderId).then((res) =>
                      (res.data.applicantDocumentUploads ?? []).map((item) => ({
                          id: item.applicantId,
                          name: item.applicantName,
                          documentsUploaded: item.documentsUploaded,
                      }))
                  )
                : getPractitionerDocumentUploads(selectedTab, from, to, serviceProviderId).then(
                      (res) =>
                          (res.data.practitionerDocumentUploads ?? []).map((item) => ({
                              id: item.practitionerId,
                              name: item.practitionerName,
                              documentsUploaded: item.documentsUploaded,
                          }))
                  );

        request
            .then((rows) => {
                if (cancelled) return;
                setUploads(rows);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(
                    err?.response?.data?.message ||
                        err.message ||
                        'Failed to load Documents Uploaded.'
                );
                setUploads([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedTab, documentsUploadedTabs, from, to, serviceProviderId]);

    const handleTabChange = (tab: DocumentsUploadedTab) => {
        if (tab === selectedTab) return;
        setSelectedTab(tab);
        setDirection(DEFAULT_DIRECTION);
    };

    const toggleSort = () => {
        setDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    };

    const sortedUploads = useMemo(() => {
        const copy = [...uploads];
        copy.sort((a, b) => {
            const diff = a.documentsUploaded - b.documentsUploaded;
            return direction === 'desc' ? -diff : diff;
        });
        return copy;
    }, [uploads, direction]);

    if (documentsUploadedTabs.length === 0 || !selectedTab) return null;

    const isClientTab = selectedTab === CLIENT_TAB;
    const subjectColumnLabel = isClientTab ? 'Client' : 'Practitioner';
    const emptyStateMessage = isClientTab
        ? 'No client document uploads for this period.'
        : 'No documents uploaded for this period.';

    return (
        <Paper
            component="section"
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, mb: 2 }}
            aria-labelledby="documents-uploaded-title"
        >
            <SectionTitle>
                <span id="documents-uploaded-title">Documents Uploaded</span>
                <InfoTooltip title={OPERATIONS_DOCUMENTS_UPLOADED} />
            </SectionTitle>
            <SectionSubtitle>
                Uploads by team member or client in the selected period.
            </SectionSubtitle>
            <AnalyticsTabs
                ariaLabel="Documents uploaded team"
                selected={selectedTab}
                onChange={handleTabChange}
                tabs={documentsUploadedTabs.map((tab) => ({
                    value: tab,
                    label: TAB_LABELS[tab],
                }))}
            />

            {loading ? (
                <AnalyticsLoading minHeight={160} />
            ) : error ? (
                <AnalyticsError message={error} />
            ) : sortedUploads.length === 0 ? (
                <AnalyticsEmpty title="No documents uploaded" description={emptyStateMessage} />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{subjectColumnLabel}</TableCell>
                                <TableCell align="right" sortDirection={direction}>
                                    <TableSortLabel
                                        active
                                        direction={direction}
                                        onClick={toggleSort}
                                    >
                                        Documents
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedUploads.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
                                    >
                                        {row.documentsUploaded}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default DocumentsUploaded;
